import { jsx as _jsx } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-09-10 17:22:11 +0200 (Thu, 10 Sep 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 165450 $
//  Purpose   : utility functions
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState, Fragment } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
// ---Framework imports
import { Actions } from 'uix/registries';
import log from 'uix/logging';
import { isSSR } from 'uix/utils';
import connect from 'uix/connect';
// ---Globals
export const createPortal = (children, targetid, hmr_gen)=>{
    /* create portal on externally managed DOM node */ if (isSSR) {
        // maybe render inplace for testing?
        return null;
    }
    let domNode = document.getElementById(targetid);
    if (domNode) {
        // ensure result is a react element by enclosing in fragment
        // if the host-react element uses proptypes to check (like sui-segment)
        // to check the type of the children, we won't get a proptype warning
        // XXX: maybe only an react react-hot-loader artefact
        return /*#__PURE__*/ _jsx(Fragment, {
            children: /*#__PURE__*/ ReactDOM.createPortal(children, domNode)
        }, `HMR_${hmr_gen}`);
    }
    // state of root.meta.portals should be checked
    log.warn('no element with id %o found', targetid);
    return null;
};
// XXX: only re-render if value of portals[target] change
// we also inject the webpack HMR generation - this seems to
// fix hot-reload probs with stuff in AutoPortal
export const AutoPortal = connect('AutoPortal', [
    'meta.portals',
    'debug.hmr.gen'
], ({ target, children, portals, gen })=>portals[target] ? createPortal(children, target, gen) : null);
/* portal: receiving end */ export const PortalTarget = connect('Portal', {
    storeMap: 'meta.portals',
    actionMap: {
        mountPortal: Actions.mountPortal
    }
}, ({ id, portals, mountPortal, style = {}, className, as: as_el = 'span' })=>{
    useEffect(()=>{
        // returned function will be called on component unmount
        return ()=>{
            mountPortal({
                [id]: false
            });
        };
    }, []);
    let props = {
        id,
        style,
        ref: ()=>portals[id] ? null : mountPortal({
                [id]: true
            }),
        className: 'portalTarget'
    };
    if (className) props.className = className;
    if (as_el == 'span') {
        return /*#__PURE__*/ _jsx("span", {
            ...props
        });
    } else {
        // ok, there is no other choice than a div
        // display: flex is fishy (but seems to be needed for dumping stuff into sui menus)
        props.style = {
            display: 'flex',
            ...props.style
        };
        return /*#__PURE__*/ _jsx("div", {
            ...props
        });
    }
});
// https://github.com/tajo/react-portal/blob/master/src/Portal.js
export const Portal = ({ node, children })=>{
    /* create portal on internally managed DOM node */ const [defaultNode, setDefaultNode] = useState(null);
    useEffect(()=>{
        if (node || isSSR) {
            return undefined;
        }
        const el = document.createElement('div');
        document.body.appendChild(el);
        setDefaultNode(el);
        return ()=>{
            document.body.removeChild(el);
        };
    }, [
        node
    ]);
    if (isSSR) {
        return null;
    }
    const target = node || defaultNode;
    if (!target) {
        // nothing to render into until the default node is attached by the effect
        return null;
    }
    return /*#__PURE__*/ ReactDOM.createPortal(children, target);
};
Portal.propTypes = {
    children: PropTypes.node.isRequired,
    node: PropTypes.any
};
